import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useAtom } from 'jotai';
import { Button, Footer, Loader } from 'src/components';
import { useOnboardingContext } from '../context';
import StepBar from '../components/StepBar';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { updateSettings } from 'src/api/functions/userApi';
import { useSchemaPresetsQuery, useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { usePlanLimitsQuery } from 'src/api/queries/subscriptionQueries';
import { AssetSchema } from 'src/types/TSchemes';
import { useCreateSchemaFromPresetsMutation } from 'src/api/mutations/schemaMutations';
import {
  useLoadDemoSamplesMutation,
  useUpdateOnboardingProgressMutation,
} from 'src/api/mutations/onboardingMutations';
import { queryNames } from 'src/api/utils/queryNames';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { SchemaPresetSummary } from 'src/types/TSchemes';
import { ImageSquare, VideoCamera, PlayBox, Eye, PuzzlePiece, Crop, Stack, ChatAI } from 'src/common/icons';
import FaceSearchIcon from 'src/common/icons/FaceSearch';
import styles from '../styles.module.scss';

// --- Addons ---

const SUPPORTED_ADDONS = [
  'ON_DEMAND_IMAGE',
  'VIDEO_TRANSCODE',
  'AI_ASSISTANT',
  'STORYBOARD',
  'FACE_RECOGNITION',
  'IMAGE_ANALYSIS',
  'REMOVE_BACKGROUND',
];

// Keep in sync with `filespin.common.constants.ADDONS_LIST.ALWAYS_ENABLED_ADDONS`
// (server side). The UI uses this to hide the toggle for these addons —
// they're always on regardless of plan.
const ALWAYS_ON_ADDONS = new Set([
  'ON_DEMAND_IMAGE',
  'VIDEO_TRANSCODE',
  'AI_ASSISTANT',
]);

const ADDON_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ON_DEMAND_IMAGE: ImageSquare,
  VIDEO_TRANSCODE: VideoCamera,
  AI_ASSISTANT: ChatAI,
  STORYBOARD: PlayBox,
  FACE_RECOGNITION: FaceSearchIcon,
  IMAGE_ANALYSIS: Eye,
  REMOVE_BACKGROUND: Crop,
};

// Field previews come from the presets endpoint (`field_count` /
// `field_names`) rather than a map maintained here. The map this replaced had
// drifted badly — it claimed E-Commerce had 8 fields against the schema's
// actual 23, and Fashion 7 against 21 — because nothing tied it to the JSON
// files it was describing.

function getSchemaLabel(preset: SchemaPresetSummary): string {
  const name = preset.name?.en || preset.schema_title || preset.preset_id;
  return name.replace(/\s+Asset Schema$/i, '');
}

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// --- Component ---

const SetupStep = () => {
  const {
    goToNextStep, goToPreviousStep, industryHint, isAdmin, submittedSchemaPresets, markPresetsSubmitted,
    samplesLoaded, setSamplesLoaded,
  } = useOnboardingContext();
  const { showAlert, showWarning } = useAlerts();
  const queryClient = useQueryClient();
  const { getErrorMessage } = useErrorMessages();

  // Addons state (admin only)
  const [addonsOpen, setAddonsOpen] = useState(false);
  const [addonsChanged, setAddonsChanged] = useState(false);
  const { isLoading: settingsLoading } = useSettingsQuery(undefined, { enabled: isAdmin });
  const [addonsSettings, updateAddonsSettings] = useAtom(addonsSettingsAtom);
  const accessToken = useAccessToken();

  // Schema state (admin only)
  const [selectedSchemas, setSelectedSchemas] = useState<Set<string>>(new Set());
  const [schemasOpen, setSchemasOpen] = useState(false);
  const { data: schemaPresetsData, isLoading: schemasLoading } = useSchemaPresetsQuery(undefined, { enabled: isAdmin });
  const { data: existingSchemasData, isLoading: existingSchemasLoading } = useAssetSchemasListQuery('ACTIVE', undefined, { enabled: isAdmin });
  // Live entitlement cap (`plan_quotas.asset_schemas` + purchased extras).
  // The presets endpoint enforces it per-schema and returns the refusals
  // INSIDE a 201, so without this the step would happily submit more than the
  // plan allows and report success — see `handleGoToNextStep`.
  const { data: planLimits, isLoading: limitsLoading } = usePlanLimitsQuery({ enabled: isAdmin });

  // Share templates are not chosen here: every preset is added for the user
  // when the wizard starts (#958, see provisionSharePresets).

  const progressMutation = useUpdateOnboardingProgressMutation();

  // Sample assets, matched to the schema chosen above, so the library is not
  // empty on first visit. On by default. Once added (here, or by the demo
  // claim before onboarding) the section says so instead of offering them —
  // it never silently disappears, e.g. on coming Back to this step.
  const offerSamples = isAdmin;
  const [addSamples, setAddSamples] = useState(true);
  const samplesMutation = useLoadDemoSamplesMutation();

  const schemaMutation = useCreateSchemaFromPresetsMutation({
    onError: (error) => showAlert(getErrorMessage(error.message)),
  });

  // Derive addon list from settings
  const addedAddons = useMemo(() => {
    if (!addonsSettings) return [];
    return Object.entries(addonsSettings)
      .filter(([key, val]: [string, any]) => val.added && SUPPORTED_ADDONS.includes(key))
      .map(([key, val]: [string, any]) => ({ key, ...val }));
  }, [addonsSettings]);

  const enabledAddonCount = useMemo(
    () => addedAddons.filter((a: any) => a.enabled).length,
    [addedAddons],
  );

  const allSchemaPresets = useMemo(() => {
    return schemaPresetsData?.data?.presets || [];
  }, [schemaPresetsData]);

  const existingSchemas: AssetSchema[] = useMemo(() => {
    return existingSchemasData?.data || [];
  }, [existingSchemasData]);

  const existingSchemaTitles = useMemo(() => {
    return new Set(existingSchemas.map((s) => s.schema.title.toLowerCase()));
  }, [existingSchemas]);

  // `quota` 0 == unlimited (project convention). `used` is the subscription-wide
  // ACTIVE count, which is what the server counts, so prefer it over
  // `existingSchemas.length` (this group only).
  const schemaQuota = planLimits?.asset_schemas?.quota ?? 0;
  const schemaUsed = planLimits?.asset_schemas?.used ?? existingSchemas.length;
  const schemaSlotsLeft = schemaQuota > 0
    ? Math.max(0, schemaQuota - schemaUsed - selectedSchemas.size)
    : Infinity;
  const atSchemaCap = schemaSlotsLeft <= 0;
  const schemaCapMessage = `Your plan includes ${schemaQuota} asset schema${schemaQuota === 1 ? '' : 's'}. `
    + 'You can browse the rest, add capacity, or swap schemas from Asset Schemas '
    + 'in Settings once you are set up.';

  const filteredSchemaPresets = useMemo(() => {
    if (existingSchemas.length === 0) return allSchemaPresets;
    return allSchemaPresets.filter(
      (p) => !existingSchemaTitles.has(p.schema_title.toLowerCase()),
    );
  }, [allSchemaPresets, existingSchemaTitles, existingSchemas]);

  // `limitsLoading` is in here so the pre-selection below never runs
  // cap-blind. If the call FAILS, isLoading goes false and the cap reads as
  // unlimited — deliberately fail-open: the server is the enforcer, and a
  // limits outage must not block onboarding.
  const adminQueriesLoading = isAdmin
    && (settingsLoading || schemasLoading || existingSchemasLoading || limitsLoading);

  // Initialize defaults
  useEffect(() => {
    if (adminQueriesLoading) return;

    // Schema: pre-select the industry hint, and NEVER more than the plan
    // allows — the entry plans (DEMO and PAYG) include exactly 1.
    //
    // `general` is deliberately NOT pre-selected. It is the FALLBACK: the
    // backend creates it lazily on first upload, and only for a group that
    // chose nothing here. Pre-selecting it would spend the only slot a
    // 1-schema plan has before the customer picked their industry — which is
    // precisely what broke signup when the schema-list fetch above used to
    // auto-provision it server-side.
    //
    // A customer who WANTS General can still tick it; it now appears in the
    // picker like any other preset, because nothing creates it behind them.
    if (isAdmin && allSchemaPresets.length > 0 && selectedSchemas.size === 0) {
      const budget = schemaQuota > 0 ? Math.max(0, schemaQuota - schemaUsed) : Infinity;
      const hinted = industryHint
        ? filteredSchemaPresets.find((p) => p.preset_id === industryHint)
        : undefined;
      if (hinted && budget > 0) setSelectedSchemas(new Set([hinted.preset_id]));
    }

    // All sections start collapsed
    setAddonsOpen(false);
    setSchemasOpen(false);
  }, [adminQueriesLoading]);

  const handleToggleAddon = (addonKey: string, enabled: boolean) => {
    if (ALWAYS_ON_ADDONS.has(addonKey)) return;
    updateAddonsSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: { [addonKey]: { enabled } },
    });
    setAddonsChanged(true);
  };

  const toggleSchema = (presetId: string) => {
    // Deselect always works; selecting past the cap does not. The server
    // refuses it anyway (inside a 201 whose errors[] nobody used to read), so
    // stop it here where we can say why.
    if (!selectedSchemas.has(presetId) && atSchemaCap) {
      showWarning(schemaCapMessage);
      return;
    }
    setSelectedSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) next.delete(presetId);
      else next.add(presetId);
      return next;
    });
  };

  const handleSelectAllSchemas = () => {
    const allIds = filteredSchemaPresets.map((p) => p.preset_id);
    const allSelected = allIds.every((id) => selectedSchemas.has(id));
    if (allSelected) {
      setSelectedSchemas(new Set());
      return;
    }
    // "Select all" means "fill the remaining slots", not "submit N and let
    // N-1 fail silently". Order follows the card order the user is looking at.
    const budget = schemaQuota > 0 ? Math.max(0, schemaQuota - schemaUsed) : Infinity;
    const next = new Set<string>();
    for (const id of allIds) {
      if (next.size >= budget) break;
      next.add(id);
    }
    setSelectedSchemas(next);
    if (budget < allIds.length) showWarning(schemaCapMessage);
  };

  const [savingAddons, setSavingAddons] = useState(false);
  const isSaving = savingAddons || schemaMutation.isLoading;

  const saveAddons = async () => {
    if (!addonsSettings) return;
    const addonPayload: Record<string, { enabled: boolean; added: boolean }> = {};
    for (const key of SUPPORTED_ADDONS) {
      const addon = addonsSettings[key];
      if (addon?.added) {
        addonPayload[key] = { enabled: addon.enabled, added: addon.added };
      }
    }
    await updateSettings({ key: 'addons', value: addonPayload }, accessToken);
  };

  // Which preset's samples to load: the schema the user just chose (the
  // industry hint first, when it is among them), else one they already have,
  // else the hint, else General. Preset ids are the sample manifest keys.
  const sampleIndustry = (createdNow: string[]): string => {
    const chosen = [...createdNow, ...Array.from(submittedSchemaPresets)];
    if (industryHint && chosen.includes(industryHint)) return industryHint;
    if (chosen.length > 0) return chosen[0];
    const existingNames = new Set(existingSchemas.map((sc) => (sc.name?.en || '').toLowerCase()));
    const existingPreset = allSchemaPresets.find((p) => existingNames.has((p.name?.en || '').toLowerCase()));
    return existingPreset?.preset_id || industryHint || 'general';
  };

  const handleGoToNextStep = async () => {
    const newSchemaIds = isAdmin
      ? Array.from(selectedSchemas).filter((id) => !submittedSchemaPresets.has(id))
      : [];

    const hasNewSchemas = newSchemaIds.length > 0;
    const wantSamples = offerSamples && addSamples && !samplesLoaded;

    if ((!isAdmin || !addonsChanged) && !hasNewSchemas && !wantSamples) {
      goToNextStep();
      return;
    }

    try {
      setSavingAddons(true);
      const promises: Promise<any>[] = [];
      const checklistUpdate: Record<string, boolean> = {};

      if (isAdmin && addonsChanged) {
        promises.push(saveAddons());
      }

      // Index of the schema promise, so its response can be inspected below —
      // POST /assetschemas/presets reports per-preset refusals INSIDE a 201.
      let schemaPromiseIndex = -1;
      if (hasNewSchemas) {
        schemaPromiseIndex = promises.length;
        promises.push(schemaMutation.mutateAsync(newSchemaIds));
        checklistUpdate.schemas_added = true;
      }

      const results = await Promise.all(promises);

      // Partial success is a 201, not a rejection: `created: 1, errors: [...]`
      // when the cap refuses the rest. `onError` never fires for it, so
      // without this the user is told nothing and walks away believing every
      // preset landed. Only mark the ones that actually did as submitted —
      // the rest stay retryable if capacity frees up.
      let createdSchemaIds = newSchemaIds;
      if (hasNewSchemas) {
        const schemaResult = results[schemaPromiseIndex];
        const errors: string[] = schemaResult?.data?.errors || [];
        const createdNames = new Set(
          (schemaResult?.data?.schemas || []).map(
            (s: { name?: { [lang: string]: string } }) => s.name?.en,
          ),
        );
        if (errors.length > 0) {
          const landed = allSchemaPresets.filter((p) => createdNames.has(p.name?.en));
          createdSchemaIds = landed.length > 0
            ? landed.map((p) => p.preset_id)
            : [];
          // The cap is the usual reason, but not the only one (a name
          // collision with an INACTIVE schema also lands here), so on an
          // unlimited plan quote the server rather than invent a cap.
          showWarning(
            `Added ${schemaResult?.data?.created ?? 0} of ${newSchemaIds.length} schemas. `
            + (schemaQuota > 0 ? schemaCapMessage : errors[0]),
          );
        }
        // The cap moved — refresh the counters this page and Settings read.
        queryClient.invalidateQueries([queryNames.planLimits]);
      }

      markPresetsSubmitted({
        schemaPresetIds: hasNewSchemas ? createdSchemaIds : undefined,
      });

      // After the schemas, so the samples are filed under the one just made.
      if (wantSamples) {
        try {
          await samplesMutation.mutateAsync({ industry: sampleIndustry(createdSchemaIds) });
          setSamplesLoaded(true);
          checklistUpdate.demo_loaded = true;
        } catch {
          // Not fatal: the "Try Agentic DAM" step loads them again
          // (the endpoint is idempotent).
        }
      }

      if (Object.keys(checklistUpdate).length > 0) {
        progressMutation.mutate({ checklist: checklistUpdate });
      }
      goToNextStep();
    } catch {
      showAlert('Something went wrong saving your settings. Please try again.');
    } finally {
      setSavingAddons(false);
    }
  };

  if (adminQueriesLoading) {
    return <Loader />;
  }

  const isSchemaHinted = (presetId: string) => industryHint === presetId;

  const existingSchemaNames = existingSchemas.map(
    (s) => s.name?.en || s.schema.title,
  );

  const selectedSchemaNames = filteredSchemaPresets
    .filter((p) => selectedSchemas.has(p.preset_id))
    .map((p) => getSchemaLabel(p));

  const allSchemaNames = [...existingSchemaNames, ...selectedSchemaNames];

  return (
    <main className={styles.step}>
      <StepBar
        onGoToNextStep={handleGoToNextStep}
        onGoToPreviousStep={goToPreviousStep}
        loadingNextStep={isSaving}
        hasUnsavedChanges={isAdmin && (selectedSchemas.size > 0 || existingSchemas.length > 0)}
      />
      <section className={styles.container}>
        <p className={styles.stepInfo}>
          Configure your account settings. We've pre-selected sensible defaults for you.
        </p>

        {/* --- Addons (admin only) --- */}
        {isAdmin && (
        <div className={styles.setupSection}>
          <div className={styles.setupSectionHeader} onClick={() => setAddonsOpen(!addonsOpen)}>
            <div className={styles.setupSectionIcon}><PuzzlePiece width={20} height={20} /></div>
            <h3>Addons</h3>
            {!addonsOpen && (
              <div className={styles.setupSectionSummary}>
                <span>{enabledAddonCount} of {addedAddons.length} enabled</span>
              </div>
            )}
            <button
              type="button"
              className={styles.setupSectionChange}
              onClick={(e) => { e.stopPropagation(); setAddonsOpen(!addonsOpen); }}
            >
              {addonsOpen ? 'Collapse' : 'Change'}
            </button>
            <span className={`${styles.setupSectionChevron} ${addonsOpen ? styles.setupSectionChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          <p className={styles.setupSectionSubtext}>
            AI-powered features. ODI, Video Transcode and AI Assistant are always on; others can be toggled anytime.
          </p>
          {addonsOpen && (
            <div className={styles.setupSectionBody}>
              <div className={styles.cardGrid}>
                {addedAddons.map((addon: any) => {
                  const isAlwaysOn = ALWAYS_ON_ADDONS.has(addon.key);
                  const isEnabled = addon.enabled;
                  const IconComponent = ADDON_ICONS[addon.key] || PuzzlePiece;
                  return (
                    <div
                      key={addon.key}
                      className={`${styles.card} ${isEnabled ? styles.cardSelected : ''}`}
                      onClick={() => !isAlwaysOn && handleToggleAddon(addon.key, !isEnabled)}
                      style={isAlwaysOn ? { cursor: 'default' } : undefined}
                    >
                      <div className={styles.cardIcon}>
                        <IconComponent width={24} height={24} />
                      </div>
                      <div className={styles.cardInfo}>
                        <h4>
                          {addon.title}
                          {isAlwaysOn && <span className={styles.cardBadge} style={{ background: 'rgba(1, 10, 44, 0.06)', color: 'rgba(1, 10, 44, 0.5)' }}>Always on</span>}
                        </h4>
                        {addon.description && <p>{addon.description}</p>}
                      </div>
                      <div className={styles.cardAction}>
                        <div className={`${styles.checkboxOuter} ${isEnabled ? styles.checkboxChecked : ''}`} style={isAlwaysOn ? { opacity: 0.5 } : undefined}>
                          {isEnabled && <CheckIcon />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        {/* --- Asset Metadata Schemas (admin only) --- */}
        {isAdmin && (
        <div className={styles.setupSection}>
          <div className={styles.setupSectionHeader} onClick={() => setSchemasOpen(!schemasOpen)}>
            <div className={styles.setupSectionIcon}><Stack width={20} height={20} /></div>
            <h3>Asset Metadata Schemas</h3>
            {(existingSchemas.length > 0 || selectedSchemas.size > 0) && !schemasOpen && (
              <div className={styles.setupSectionSummary}>
                <span>
                  {allSchemaNames.length <= 2
                    ? allSchemaNames.join(', ')
                    : `${allSchemaNames.slice(0, 2).join(', ')} +${allSchemaNames.length - 2}`}
                </span>
              </div>
            )}
            <button
              type="button"
              className={styles.setupSectionChange}
              onClick={(e) => { e.stopPropagation(); setSchemasOpen(!schemasOpen); }}
            >
              {schemasOpen ? 'Collapse' : 'Change'}
            </button>
            <span className={`${styles.setupSectionChevron} ${schemasOpen ? styles.setupSectionChevronOpen : ''}`}>
              ▾
            </span>
          </div>
          <p className={styles.setupSectionSubtext}>
            Organize assets with structured metadata. Optional — set up or customize anytime from Settings.
          </p>
          {schemasOpen && (
            <div className={styles.setupSectionBody}>
              {(existingSchemas.length > 0 || filteredSchemaPresets.length > 0) && (
                <>
                  <div className={styles.sectionToolbar}>
                    <span className={styles.sectionToolbarLabel}>
                      {schemaQuota > 0
                        ? (schemaUsed + selectedSchemas.size >= schemaQuota
                          ? `All ${schemaQuota} schema${schemaQuota === 1 ? '' : 's'} in use`
                          : `${schemaUsed + selectedSchemas.size} of ${schemaQuota} schema${schemaQuota === 1 ? '' : 's'} used`)
                        : (
                          <>
                            {existingSchemas.length > 0 && `${existingSchemas.length} added`}
                            {existingSchemas.length > 0 && filteredSchemaPresets.length > 0 && ', '}
                            {filteredSchemaPresets.length > 0 && `${filteredSchemaPresets.length} more available`}
                          </>
                        )}
                    </span>
                    {filteredSchemaPresets.length > 0 && (
                      <button type="button" className={styles.sectionToolbarAction} onClick={handleSelectAllSchemas}>
                        {filteredSchemaPresets.every((p) => selectedSchemas.has(p.preset_id)) ? 'Deselect all' : 'Select all'}
                      </button>
                    )}
                  </div>
                  <div className={styles.cardGrid}>
                    {existingSchemas.map((schema) => {
                      const fieldCount = Object.keys(schema.schema.properties || {}).length;
                      const schemaName = schema.name?.en || schema.schema.title;
                      return (
                        <div
                          key={schema.id}
                          className={`${styles.card} ${styles.cardSelected}`}
                          style={{ cursor: 'default', opacity: 0.75 }}
                        >
                          <div className={styles.cardInfo}>
                            <h4>
                              {schemaName}
                              <span className={styles.cardBadge} style={{ background: 'rgba(1, 10, 44, 0.06)', color: 'rgba(1, 10, 44, 0.5)' }}>
                                Already Added by Administrator
                              </span>
                            </h4>
                            {fieldCount > 0 && (
                              <div className={styles.cardFieldPreview}>
                                {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                          <div className={styles.cardAction}>
                            <div className={`${styles.checkboxOuter} ${styles.checkboxChecked}`} style={{ opacity: 0.5 }}>
                              <CheckIcon />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {filteredSchemaPresets.map((preset) => {
                      const isSelected = selectedSchemas.has(preset.preset_id);
                      const isRecommended = isSchemaHinted(preset.preset_id);
                      // Out of slots: the server would refuse this one anyway.
                      // Already-selected cards stay live so they can be
                      // deselected to free the slot for a different preset.
                      const isCapped = atSchemaCap && !isSelected;
                      return (
                        <div
                          key={preset.preset_id}
                          className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`}
                          onClick={() => toggleSchema(preset.preset_id)}
                          title={isCapped ? schemaCapMessage : undefined}
                          style={isCapped ? { cursor: 'not-allowed', opacity: 0.5 } : undefined}
                        >
                          <div className={styles.cardInfo}>
                            <h4>
                              {getSchemaLabel(preset)}
                              {isRecommended && <span className={styles.cardBadge}>Recommended</span>}
                            </h4>
                            <p>{preset.description}</p>
                            {Boolean(preset.field_count) && (
                              <div className={styles.cardFieldPreview}>
                                {preset.field_count} fields
                                {preset.field_names && preset.field_names.length > 0
                                  && ` — adds ${preset.field_names.join(', ')}…`}
                              </div>
                            )}
                          </div>
                          <div className={styles.cardAction}>
                            <div className={`${styles.checkboxOuter} ${isSelected ? styles.checkboxChecked : ''}`}>
                              {isSelected && <CheckIcon />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {existingSchemas.length === 0 && filteredSchemaPresets.length === 0 && (
                <p style={{ textAlign: 'center', color: 'rgba(1, 10, 44, 0.5)', marginTop: '1rem' }}>
                  No schema presets are currently available. You can add schemas later from Settings.
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* --- Sample assets (admin only) --- */}
        {offerSamples && (
        <div className={styles.setupSection}>
          <div className={styles.setupSectionHeader} style={{ cursor: 'default' }}>
            <div className={styles.setupSectionIcon}><ImageSquare width={20} height={20} /></div>
            <h3>Sample assets</h3>
          </div>
          <div className={styles.setupSectionBody}>
            {samplesLoaded ? (
              <div
                className={`${styles.card} ${styles.cardSelected}`}
                style={{ cursor: 'default' }}
                data-testid="onboarding-samples-added"
              >
                <div className={styles.cardInfo}>
                  <h4>Sample assets added</h4>
                  <p>
                    They're in your library, tagged <code>demo-sample</code>. Delete them any time.
                  </p>
                </div>
                <div className={styles.cardAction}>
                  <div className={`${styles.checkboxOuter} ${styles.checkboxChecked}`} style={{ opacity: 0.5 }}>
                    <CheckIcon />
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.card} ${addSamples ? styles.cardSelected : ''}`}
                onClick={() => setAddSamples(!addSamples)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setAddSamples(!addSamples);
                  }
                }}
                role="checkbox"
                tabIndex={0}
                aria-checked={addSamples}
                data-testid="onboarding-add-samples"
              >
                <div className={styles.cardInfo}>
                  <h4>Add a few sample assets so you can start exploring right away</h4>
                  <p>
                    A handful of images and a short video, filed under your schema and tagged
                    {' '}<code>demo-sample</code>. Delete them any time.
                  </p>
                </div>
                <div className={styles.cardAction}>
                  <div className={`${styles.checkboxOuter} ${addSamples ? styles.checkboxChecked : ''}`}>
                    {addSamples && <CheckIcon />}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

      </section>
      <div className={styles.setupBottomCta}>
        <Button size="small" onClick={handleGoToNextStep} loading={isSaving}>
          Save and continue
        </Button>
      </div>
      <Footer />
    </main>
  );
};

export default SetupStep;
